// seo-schema/vertical-data.ts
//
// Per-vertical page data. SOURCE OF TRUTH: _context/schema-sot/03_Verticals.csv
// (descriptions) + 04_FAQ.csv (FAQs). Descriptions marked DRAFT-SEED in the CSVs
// are placeholders until the per-vertical prose pass (spec §5) — flight first.
// Pure data, zero imports.

export interface FaqItem {
  q: string;
  a: string;
}

export interface VerticalData {
  key: string;
  name: string;
  /** Locale-less landing path with leading slash, e.g. "/flight". */
  path: string;
  serviceType: string;
  description: string;
  faq: FaqItem[];
}

// Cross-vertical FAQs — rendered on the homepage / About, not per vertical.
export const GLOBAL_FAQ: FaqItem[] = [
  {
    q: "How can I pay for a booking in Myanmar without a credit card?",
    a: "Flymya accepts 29 payment methods including Myanmar mobile wallets (KBZPay, Wave Pay, AYA Pay, CB Pay, Citizens Pay, uabpay, A+ Wallet, OnePay, MPT Pay, MoMoney), local MPU cards, and cash over the counter at 123, NearMe and TrueMoney agents nationwide. International Visa and Mastercard are also accepted.",
  },
  {
    // Location wording aligned 2026-08-16 with ops (2026-08-12) and Emmy's copy:
    // offices in Yangon + Mandalay, retail counters incl. City Square Taunggyi.
    // Previously said "four offices in Yangon, one in Mandalay" — stale, no Taunggyi.
    q: "Is Flymya a licensed travel agency?",
    a: "Yes. Flymya has operated since 2015, is an IATA-accredited agent (code 05300013) and a PATA member, with offices in Yangon and Mandalay, retail counters at Junction City, Myanmar Plaza and Hledan Centre in Yangon and City Square in Taunggyi, and 24/7 phone support on +95 9 880 441 011.",
  },
  {
    q: "Can I book Flymya inside my banking app?",
    a: "Yes. Flymya runs as a mini-app inside KBZPay (Travel section), Wave Pay (Tickets), AYA Pay, CB Pay, uabpay, A+ and Citizens Pay — open your wallet app and look for Flymya to book without leaving the app.",
  },
];

export const VERTICALS: VerticalData[] = [
  {
    key: "flight",
    name: "Flights",
    path: "/flight",
    serviceType: "Flight ticket booking",
    // Wave 2b Part A — promoted from _context/AI-Friendly-SEO/content/flight-prose.md.
    // Tone approved by the PO; the domestic carrier list confirmed by OPS 2026-08-14.
    // The visible page copy (H1, intro, trust block, FAQ accordion) is Part B and is
    // FRONT-END work — see WAVE_2B_SCOPE.md. This data alone does NOT fix the
    // "Google chose different canonical" duplicate verdict; only visible copy does.
    // Emmy (ops/marketing) rewrote this copy 2026-08-16 and her version is the base —
    // it leads with search intent ("search, compare and book") rather than describing
    // the company, matching measured GSC demand. Carrier lists confirmed by ops
    // (domestic 08-14, international via Emmy's written copy 08-16).
    // NOTE: the visible trust block says "29 payment methods" (marketing's call); that
    // count is deliberately NOT repeated here — our own paymentAccepted lists 15, so
    // the number can't be enumerated from our data, and the company-wide description
    // already shipped count-free.
    description:
      "Search, compare and book domestic and international flights from Myanmar with Flymya. Compare schedules and fares from Myanmar Airways International, Mingalar (formerly Air KBZ), Myanmar National Airlines, Mann Yadanarpon and Air Thanlwin, plus international airlines on routes such as Yangon–Bangkok, Yangon–Singapore and Yangon–Kuala Lumpur. Pay with Myanmar mobile wallets, MPU cards or international Visa and Mastercard, and receive your e-ticket by email or SMS.",
    faq: [
      {
        q: "How can I book domestic flights in Myanmar?",
        a: "Search, compare and book Myanmar domestic flights online with Flymya. Enter your departure city, destination and travel dates to compare available airlines, schedules and fares in one place, on routes connecting Yangon, Mandalay, Nyaung U (Bagan), Heho (Inle Lake) and other destinations across Myanmar. Pay online with KBZPay, Wave Pay, AYAPay, MPU, Visa or Mastercard and receive your e-ticket after payment. Flymya has been helping travellers book flights since 2015, is an IATA-accredited travel agent, and provides 24/7 customer support.",
      },
      {
        // ⚠️ Both carrier lists are commercial claims. Domestic confirmed by ops
        // 2026-08-14; international confirmed by Emmy's written copy 2026-08-16.
        // Re-verify with ops before any future edit.
        q: "Which domestic and international airlines can I book with Flymya?",
        a: "For domestic travel, airlines include Myanmar Airways International (MAI), Mingalar (formerly Air KBZ), Myanmar National Airlines (MNA), Mann Yadanarpon and Air Thanlwin. For international travel, popular airlines include Singapore Airlines, Emirates, Thai Airways, AirAsia and Malaysia Airlines, along with other airlines available depending on your destination and travel date. Search your route on Flymya to see the latest available airlines, schedules and fares.",
      },
      {
        q: "How much baggage is included on Myanmar domestic flights?",
        a: "Baggage allowance for Myanmar domestic flights depends on the airline and fare class you book. Domestic fares typically include around 20 kg of checked baggage and 5 kg of cabin baggage, but the exact allowance can vary. Always check the allowance shown for your selected flight and fare during booking, before completing payment.",
      },
      {
        q: "Can I pay for flight tickets with KBZPay, Wave Pay or AYAPay?",
        a: "Yes. Flymya accepts popular Myanmar mobile wallets including KBZPay, Wave Pay and AYAPay for flight bookings, alongside local MPU cards, international Visa and Mastercard, and selected over-the-counter cash payment options including 123, NearMe and TrueMoney.",
      },
      {
        q: "Can I book a flight with an international Visa or Mastercard?",
        a: "Yes. Flymya accepts international Visa and Mastercard for domestic and international flight bookings. International travellers can search for flights, select their preferred fare and complete the booking online.",
      },
      {
        q: "How do I receive my flight e-ticket after booking?",
        a: "After your payment is confirmed, your flight e-ticket is issued and sent to you by email or SMS. You do not need to visit an office to collect a printed ticket — keep the e-ticket on your phone and bring the required identification and travel documents when checking in.",
      },
      {
        q: "Can I book international flights from Myanmar with Flymya?",
        a: "Yes. Popular international routes include Yangon–Bangkok, Yangon–Singapore and Yangon–Kuala Lumpur, with additional destinations and airlines available depending on your travel dates. Search your destination to compare available international flights, schedules and fares before booking.",
      },
    ],
  },
  {
    key: "bus",
    name: "Bus tickets",
    path: "/bus",
    serviceType: "Bus ticket booking",
    // Facts confirmed by OPS (Aye) 2026-08-18, gaps BUS-1…BUS-5.
    // Operator COUNT resolved by OPS 2026-08-24: her earlier "50" was superseded
    // by "104, or let's just say more than 100". data_bus_operators.csv has 102.
    // "100+" is published because it is true under both figures (102 and 104) and
    // does not go stale as operators are added or removed.
    description:
      "Compare Myanmar long-distance bus operators, routes, seat classes and fares in one search. Book with 100+ operators including Shwe Mandalar, Mandalar Min, Myat Mandalar Htun, GI Express Group, Shwe Mandalay, Khaing Mandalay, High Class and Shwe Sin Set Kyar across 100+ routes. VIP, sleeper and air-conditioned coaches from 25,000 MMK — pick your exact seat and receive an instant e-ticket.",
    faq: [
      {
        q: "How do I book a bus ticket in Myanmar online?",
        a: "Search your route and travel date on Flymya to compare operators, departure times, seat classes and fares in one place. Choose your bus, pick your exact seat from the live seat plan, pay online with a Myanmar mobile wallet, MPU card or international Visa/Mastercard, and receive your e-ticket by email or SMS.",
      },
      {
        q: "Which bus operators can I book with Flymya?",
        a: "Flymya sells tickets for 100+ Myanmar long-distance operators, including Shwe Mandalar, Mandalar Min, Myat Mandalar Htun, GI Express Group, Shwe Mandalay, Khaing Mandalay, High Class and Shwe Sin Set Kyar, covering 100+ routes across the country.",
      },
      {
        q: "How much does a bus ticket in Myanmar cost?",
        a: "Bus fares booked through Flymya start from 25,000 MMK, depending on the route, operator and seat class. The exact fare is shown before you pay.",
      },
      {
        q: "What types of bus can I book?",
        a: "Flymya offers VIP, sleeper and air-conditioned coaches, including 2×1 seating for extra space on overnight journeys. The classes available for your route are shown when you search.",
      },
      {
        q: "Can I choose my seat when booking a bus?",
        a: "Yes — Flymya shows each bus's live seat plan so you pick your exact seat, then pay online and receive an instant e-ticket.",
      },
    ],
  },
  {
    key: "hotel",
    name: "Hotels",
    path: "/hotel",
    serviceType: "Hotel booking",
    // Facts confirmed by OPS (Aye) 2026-08-24, gaps H-1…H-3.
    //
    // ⚠️ COUNT — read this before changing "over 200". OPS first said ~200, then
    // second-guessed it against data_hotels.csv's 2,579 rows and suggested
    // "thousands". The row count is NOT Myanmar inventory: parsing the file gives
    // 1,154 rows with country=mm (the rest are my/ua/th/cn/…), of which 238 are
    // flagged is_hotel=true. Her original ~200 matches the is_hotel figure.
    // "over 200" is published because it is true under BOTH readings (238 and
    // 1,154), so it cannot be falsified downward. Do not raise it to "thousands"
    // — that is only defensible if the whole file is Myanmar, and it is not.
    //
    // ⚠️ INTERNATIONAL — OPS cites 1.7M properties. Deliberately published without
    // the number: that is supplier catalogue inventory, not ours, and we could not
    // produce the list if challenged. Add the figure only with attribution.
    //
    // Long-stay PRICES: unit confirmed PER NIGHT by OPS 2026-08-24 (the open H-4
    // question). Published as a single "from" figure — the lowest of the three
    // quoted rates (Zealax 150,000; Mercure 160,000; Lotte 260,000) — rather than
    // per property, matching the balloon precedent, so the copy does not go stale
    // when one property repricess. Do NOT publish a derived monthly total: monthly
    // rates are commonly discounted off nightly, so nightly x 30 would overstate.
    description:
      "Book hotels across Myanmar and worldwide — over 200 properties in Myanmar, from Yangon business hotels to Bagan boutique stays and Inle Lake resorts, plus international hotels through our partner network. Long-stay rates at Yangon serviced apartments and hotels start from 150,000 MMK per night, for stays of one month or more.",
    faq: [
      {
        q: "How many hotels can I book in Myanmar through Flymya?",
        a: "Flymya lists over 200 hotels across Myanmar, covering Yangon, Mandalay, Bagan, Inle Lake and Ngapali, from budget guesthouses to five-star properties. International hotels worldwide are also bookable through our partner network.",
      },
      {
        q: "Can I book long-stay accommodation?",
        a: "Yes — Flymya offers long-stay rates at Yangon hotels and serviced apartments, with a minimum stay of one month. Nightly bookings are available separately across Myanmar and worldwide.",
      },
      {
        q: "What is the minimum stay for a long-stay booking?",
        a: "One month. Stays shorter than a month are booked at standard nightly hotel rates instead.",
      },
      {
        q: "How much does long-stay accommodation in Yangon cost?",
        a: "Long-stay rates start from 150,000 MMK per night, depending on the property and room type, with a minimum stay of one month. The exact rate is confirmed at booking time.",
      },
      {
        // ⚠️ OPS-confirmed names ONLY. data_hotels.csv flags 12 rows is_long_stay=true
        // (adding Sedona, Kaung, Inya Lake, Yangon Thanlwin, both Best Westerns,
        // Hotel Parami, T.Hotel), but OPS named just four — Mercure, Lotte, Zealax
        // and Spring Line — and only those four carry quoted rates. Publishing the
        // DB flag would repeat the same mistake as the "2,579 hotels" count: a
        // column is not a commercial offer. Spring Line is omitted — OPS reports it
        // TEMPORARILY CLOSED (2026-08-24). Widen only if OPS confirms the other 8.
        q: "Which properties offer long-stay rates?",
        a: "Long-stay rates are available at Mercure Yangon Kaba Aye, Lotte Serviced Apartment and Zealax Hotel & Residence, with further properties added over time. Availability and rates are confirmed at booking time.",
      },
      {
        q: "How do I book a hotel in Myanmar online?",
        a: "Search your destination and dates on Flymya to compare properties, room types and rates in one place. Choose your room, pay online with a Myanmar mobile wallet, MPU card or international Visa/Mastercard, and receive your confirmation by email.",
      },
      {
        // ⚠️ CORRECTED 2026-09-01. The previous answer was FALSE, and it was
        // live on production in llms-full.txt.
        //
        // It read: "Yes — Flymya accepts international Visa and Mastercard
        // alongside Myanmar payment methods, so hotels can be booked from
        // abroad." The product blocks that path. In
        // src/components/web/NationalityRadioWebV3.js the "Myanmar Citizen: No"
        // radio renders with `disabled={true}` when pageName === "Hotel", under
        // a tooltip that reads "Currently unavailable ... please call our
        // 24-hours hotline". A non-Myanmar citizen cannot search a hotel at
        // all, so no payment method is reachable.
        //
        // The question also changed. "From outside the country" conflates
        // location with citizenship, and the control is citizenship. A Myanmar
        // citizen abroad books online without a problem.
        //
        // ⚠️ BUS CARRIES THE SAME BLOCK (pageName === "bus", line 98). The bus
        // copy survives, because it claims payment methods and never claims
        // that a foreigner can book. Re-read it before you widen any claim.
        //
        // Restore an online answer only when the radio is enabled again. The
        // tooltip says "currently", so this is a temporary product state.
        q: "Can I book a hotel if I am not a Myanmar citizen?",
        a: "Not online today. Flymya restricts online hotel booking to Myanmar citizens. Call +95 9 880 441 011 at any hour, and the Flymya team books the hotel for you.",
      },
    ],
  },
  {
    key: "balloon",
    name: "Balloon rides",
    path: "/balloon",
    serviceType: "Hot-air balloon ride booking",
    // Facts confirmed by OPS (Aye) 2026-08-18, gaps B-1…B-7. The four operator
    // names independently match _list_of_operators/data_balloon_operators.csv.
    // Prices published as a "from" figure (STT is lowest at 624,000) rather than
    // per-operator, so the page doesn't go stale when one operator changes.
    // Oct–Apr season CONFIRMED by OPS (Aye) 2026-08-24 — previously carried from
    // schema-sot unverified. GSC: "myanmar balloon rides" = 141 impressions @ pos 19.5.
    description:
      "Compare and book Bagan hot air balloon rides with Balloon over Bagan, STT Ballooning, Oriental Ballooning and Golden Eagle Balloon. Sunrise flights of about 45 minutes over the temple plain, from 624,000 MMK per person, including private hotel pick-up, light breakfast, a champagne celebration on landing and a signed flight certificate.",
    faq: [
      {
        q: "How much does a hot air balloon ride in Bagan cost?",
        a: "Bagan balloon flights booked through Flymya start from 624,000 MMK per person, depending on the operator you choose. The exact price for your selected flight and date is shown during booking, before you pay.",
      },
      {
        q: "Which balloon operators can I book with Flymya?",
        a: "Flymya books flights with Balloon over Bagan, STT Ballooning, Oriental Ballooning and Golden Eagle Balloon. You can compare operators, prices and availability in one search.",
      },
      {
        q: "How long is a Bagan balloon flight?",
        a: "A Bagan balloon flight lasts approximately 45 minutes in the air. Allow around three to four hours in total, including hotel pick-up before sunrise, the pre-flight briefing, the flight itself and the celebration after landing.",
      },
      {
        q: "Is hotel pickup included with a Bagan balloon ride?",
        a: "Yes. Every booking includes pick-up and drop-off in a private vehicle between your Bagan hotel and the launch site.",
      },
      {
        q: "What is included in a Bagan balloon flight?",
        a: "Your booking includes pick-up and drop-off in a private vehicle, a light breakfast before take-off, a champagne celebration after landing, and a personalised flight certificate signed by your pilot.",
      },
      {
        q: "What happens if my balloon flight is cancelled because of the weather?",
        a: "Ballooning is weather-dependent. If your flight cannot operate for safety reasons, inclement or unfavourable weather, or operational difficulties, your booking is refunded in full or you can change to another date.",
      },
      {
        q: "When do Bagan balloon rides operate?",
        a: "Bagan balloon flights operate seasonally, from October to April, with sunrise departures. Availability and prices are confirmed at booking time.",
      },
    ],
  },
  {
    key: "car-rental",
    name: "Car rental",
    path: "/car-rental",
    serviceType: "Car rental with driver",
    // Facts confirmed by OPS (Aye) 2026-08-18, gaps C-1…C-6 — no open gaps.
    // OPS confirmed WITH-DRIVER ONLY (the seed draft had assumed self-drive
    // might exist), so the copy states that plainly rather than staying silent
    // and misleading anyone searching for self-drive hire.
    description:
      "Book a private car with driver in Myanmar. Saloon cars and vans for airport transfers from 120,000 MMK, day return trips from 250,000 MMK, and multi-day domestic or international journeys, covering Yangon, Mandalay, Nay Pyi Taw, Bago, the Delta region, Southern Shan State and the Tanintharyi Region.",
    faq: [
      {
        q: "Can I rent a self-drive car in Myanmar with Flymya?",
        a: "No — all Flymya car rentals include a professional driver. This is the standard arrangement for visitors in Myanmar, and it means you do not need a local licence, local insurance or local navigation to get around.",
      },
      {
        q: "What vehicles can I book?",
        a: "Flymya offers saloon cars and vans, so you can choose based on your group size and luggage.",
      },
      {
        q: "How much does car rental with driver cost in Myanmar?",
        a: "Airport transfers start from 120,000 MMK and day return trips from 250,000 MMK. Multi-day hire is priced by itinerary. The exact price for your booking is confirmed before you pay.",
      },
      {
        q: "Which cities and regions does Flymya cover?",
        a: "Flymya arranges cars with drivers in Yangon, Mandalay, Nay Pyi Taw, Bago, the Delta region, Southern Shan State and the Tanintharyi Region, plus multi-day journeys between them.",
      },
      {
        q: "What kinds of trip can I book?",
        a: "Airport transfers, day return trips, and multi-day hire for both domestic and international travel — whether you need a single business transfer or a driver for a touring itinerary.",
      },
    ],
  },
  {
    key: "tour-packages",
    name: "Tour packages",
    path: "/tour-packages",
    serviceType: "Tour package booking",
    // Facts confirmed by OPS (Aye) 2026-08-18, gaps T-1…T-6. This vertical had
    // NO citable content before — named itineraries with durations, inclusions
    // and prices are exactly what AI assistants quote.
    // ⚠️ T-1 lists an India package but T-5's destination list omits India;
    // India is included here on the strength of T-1. Confirm with OPS.
    description:
      "Private, customisable tour packages from Myanmar. Domestic itineraries from 450,000 MMK per person cover Ngwe Saung, Chaung Tha, Bagan, Inle Lake, Taunggyi, Kalaw and Kawthaung; international packages from 2,480,000 MMK cover Thailand, Vietnam, China and India. Every package includes flights, accommodation, ground transportation, meals, a tour leader, a guide, entrance fees and insurance.",
    faq: [
      {
        q: "What is included in a Flymya tour package?",
        a: "Every Flymya tour package includes return flights, accommodation, ground transportation, meals, a tour leader, a local guide, entrance fees and travel insurance. The price quoted covers the whole trip rather than the booking alone.",
      },
      {
        q: "How much does a tour package cost?",
        a: "Domestic packages within Myanmar start from 450,000 MMK per person, and international packages start from 2,480,000 MMK per person. The exact price depends on your destination, duration, travel dates and party size.",
      },
      {
        q: "Which destinations can I visit on a Myanmar tour package?",
        a: "Domestic tour packages cover Ngwe Saung, Chaung Tha, Bagan, Inle Lake, Taunggyi, Kalaw and Kawthaung. Popular itineraries include Yangon–Ngwe Saung–Yangon (2 nights / 3 days) and Yangon–Kalaw–Taunggyi–Inle (3 nights / 4 days).",
      },
      {
        q: "Does Flymya offer international tour packages from Myanmar?",
        a: "Yes. International packages include Thailand (3 nights / 4 days), Vietnam (4 nights / 5 days), China (5 nights / 6 days) and India (4 nights / 5 days), visiting destinations such as Bangkok, Phuket, Chiang Mai, Da Nang, Hanoi and Halong Bay.",
      },
      {
        q: "Are Flymya tours private or group tours?",
        a: "All Flymya tour packages are private, arranged for your party only, and the itinerary can be customised around your dates, interests and budget.",
      },
      {
        q: "Can I customise a tour package?",
        a: "Yes. Packages are a starting point rather than a fixed itinerary — durations, destinations and inclusions can be adjusted. Contact the Flymya team on +95 9 880 441 011, available 24/7, to arrange a package around your plans.",
      },
    ],
  },
  {
    key: "travel-services",
    name: "Visa & VIP services",
    path: "/travel-services/visa-service",
    serviceType: "Visa assistance service",
    description:
      "Myanmar visa assistance and VIP airport services — document guidance, application handling and fast-track arrival support.",
    faq: [
      {
        q: "Do I need a visa to visit Myanmar?",
        a: "Most visitors need a visa or eVisa. Flymya's visa service guides you through requirements and handles the application. Rules vary by nationality — requirements are confirmed during the service.",
      },
    ],
  },
  {
    key: "insurance",
    name: "Travel insurance",
    path: "/insurance",
    serviceType: "Travel insurance booking",
    description:
      "Travel insurance for Myanmar and international trips — coverage for flight changes, medical events and baggage, bookable online.",
    faq: [
      {
        q: "What does Flymya travel insurance cover?",
        a: "Coverage options include medical events, trip changes and baggage. Exact coverage depends on the selected policy — details are shown before purchase.",
      },
    ],
  },
];
